import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { scrollIntoViewWithinContainer } from '@/shared/lib';

/** Строка блока заключения: сам блок и последняя строка его поддерева. */
export type ShownBlockRow = {
  blockId: number;
  /** Блок последней строки поддерева — ею заканчивается показанный целиком блок */
  lastSubtreeBlockId: number;
};

/** Наведение заключения на блок, показанный целиком. */
export type ShownBlockScroll = {
  /** Ref строки блока: по строкам считается, помещается ли раскрытый блок в области просмотра */
  attachRow: (row: ShownBlockRow, element: HTMLElement | null) => void;
};

/** Строка блока в разметке заключения вместе с концом её поддерева. */
type AttachedRow = ShownBlockRow & { element: HTMLElement };

/**
 * Ставит наименование блока, показанного целиком, к верхнему краю области просмотра заключения —
 * но только когда раскрытый блок в ней целиком не помещается.
 *
 * Прокрутка идёт после раскрытия, а не в момент нажатия: у блока, в котором отбор скрыл всё,
 * содержимое разворачивается ниже видимой части, и до раскрытия заключению просто некуда
 * прокручиваться — нажатие выглядело бы как «ничего не произошло». Когда же раскрытое и так видно
 * целиком, прокрутка только увела бы взгляд с разбираемого места. Строки заключения строит другой
 * компонент, поэтому цели приходят ref-ами.
 */
export const useShownBlockScroll = (
  shownBlockId: number | null,
  viewportRef: RefObject<HTMLDivElement | null>,
): ShownBlockScroll => {
  const rowsRef = useRef(new Map<number, AttachedRow>());

  const attachRow = useCallback((row: ShownBlockRow, element: HTMLElement | null) => {
    if (element) rowsRef.current.set(row.blockId, { ...row, element });
    else rowsRef.current.delete(row.blockId);
  }, []);

  useEffect(() => {
    const container = viewportRef.current;
    if (shownBlockId === null || !container) return;
    const shownRow = rowsRef.current.get(shownBlockId);
    if (!shownRow) return;

    const lastRow = rowsRef.current.get(shownRow.lastSubtreeBlockId) ?? shownRow;
    const containerRect = container.getBoundingClientRect();
    const headingRect = shownRow.element.getBoundingClientRect();
    const isBlockShownWhole =
      headingRect.top >= containerRect.top && lastRow.element.getBoundingClientRect().bottom <= containerRect.bottom;
    if (isBlockShownWhole) return;

    // Наименование подводится к краю вместе с отделяющей его границей: прижатое к самому краю, оно
    // читается как обрезанное. Разделители у строк одной толщины, поэтому она берётся у самой строки
    const gap = Number.parseFloat(getComputedStyle(shownRow.element).borderBottomWidth) || 0;
    scrollIntoViewWithinContainer(shownRow.element, { align: 'start', behavior: 'smooth', container, gap });
  }, [shownBlockId, viewportRef]);

  return { attachRow };
};
